/**
 * rebuy-cart-bridge.js — For Rowan
 * Adapted from londontownusa/Rebuy Cart Fix/assets/rebuy-cart-bridge.js (verified 2026-06-30).
 * SOLUTIONS-143
 * ---------------------------------------------------------------------------
 * Single source of truth for "reveal the cart after an add-to-cart".
 *
 * WHY (measured 2026-07-30, Playwright, 4x CPU):
 *   The theme adds via jQuery XHR (`largePopdown` → $.ajax POST /cart/add.js).
 *   Rebuy wraps `fetch`, not XHR, so it never sees the add. Rebuy only notices
 *   when the theme's `complete` handler runs its own `fetch('/cart.js')`, which
 *   is why the cart takes ~1s to appear even when Smart Cart has been `ready`
 *   for 12 seconds:
 *
 *     click                                +0ms
 *     XHR POST /cart/add.js sent          +85ms
 *     rebuy:cart.change (Rebuy notices)   +889ms   <-- the cost
 *     rebuy:smartcart.show               +896ms
 *     flyout painted                     +988ms
 *
 *   Rebuy's own show() → paint is only ~90-120ms, so the fix is to stop waiting for
 *   Rebuy to notice — but ONLY when it is safe to do so.
 *
 * THE REVEAL IS CONDITIONAL. Smart Cart renders from its own cart state, which does not
 * contain the new line item until ~1.7-2.0s after the add on this theme:
 *
 *   Cart ALREADY had items -> open immediately. The existing items render at once, so
 *     the drawer is truthful, and the new line joins when Rebuy catches up.
 *   Cart was EMPTY         -> do NOT open. Anything we do early paints "YOUR CART IS
 *     EMPTY" at the exact moment the shopper added something. Rebuy opens the cart
 *     itself, populated, at ~1.8s — the same as today. The win on this path is the
 *     button spinner, not speed.
 *
 * Do not "simplify" either branch; both were measured. See deferToRebuy() below.
 *
 * Fallback ladder — this theme has NO native cart drawer and Smart Cart is configured
 * with `disable_cart_page: true`, so there is nowhere else to send the shopper:
 *   ready            -> show() now
 *   not ready yet    -> bounded wait, then show()
 *   wait expired     -> `fallback` mode (default: hand back to the ATC feedback
 *                       layer, which confirms the add on the button so the click
 *                       is never silently dead)
 *
 * Uses ONLY documented Rebuy APIs: Rebuy.SmartCart.show()/.status,
 * Rebuy.Cart.fetchCart(). Safe no-op when Rebuy is absent.
 */
(function () {
  'use strict';

  var CFG = window.RebuyCartBridgeConfig || {};
  if (CFG.enabled === false) return;

  var ATC_SELECTOR = CFG.atcSelector ||
    '[data-add-to-cart], .btn--add-to-cart, form[action*="/cart/add"] button[name="add"]';
  var CART_ICON_SELECTOR = CFG.cartIconSelector ||
    '.js-drawer-open-cart, .navlink--cart, a[href$="/cart"], [data-cart-open], [data-rebuy-cart-toggle]';

  // 'add-sent'    — reveal when the add XHR is in flight (default; ~85ms, and
  //                 guarantees an add is genuinely happening before we open).
  //                 Only useful where the theme adds via XHR (Pipeline/jQuery).
  // 'click'       — reveal on the raw click (fastest, but opens even if the add 422s)
  // 'cart-change' — wait for Rebuy to notice (the current, slow behaviour)
  // 'manual'      — install no trigger; the caller drives reveal() explicitly.
  //                 Use on Dawn-family themes that add via `fetch` (Rebuy already
  //                 sees those) and call the bridge from product-form.js.
  var REVEAL_ON = CFG.revealOn || 'add-sent';

  // 'confirm' — hand back to rebuy-atc-feedback.js (default; safe here)
  // 'cart'    — window.location.assign('/cart')  [only if /cart actually renders;
  //             Smart Cart has disable_cart_page: true, so verify before using]
  // 'none'    — do nothing
  var FALLBACK = CFG.fallback || 'confirm';

  var READY_TIMEOUT_MS = CFG.readyTimeoutMs || 5000; // no native drawer to fall into
  var READY_POLL_MS = CFG.readyPollMs || 100;
  var REVEAL_DEBOUNCE_MS = CFG.revealDebounceMs || 120;
  var RESYNC_DELAY_MS = CFG.resyncDelayMs || 0;

  var revealTimer = null;
  var waiting = false;
  var lastRevealAt = 0;
  var lastAddAt = 0;          // when we last saw a genuine add-to-cart
  var pendingOnRevealed = [];

  // How recently an add must have happened for a cart-change to count as "that add".
  // Rebuy fires `rebuy:cart.change` SEVERAL TIMES during its own initialisation
  // (observed at +6.1s, +8.1s, +9.4s on this store with no user interaction). Without
  // this gate the safety-net listeners below treat those as an add and pop the Smart
  // Cart open on page load — which also puts its overlay over the ATC button.
  var ADD_WINDOW_MS = CFG.addWindowMs || 6000;

  function sawRecentAdd() { return lastAddAt > 0 && Date.now() - lastAddAt < ADD_WINDOW_MS; }

  /**
   * Did the cart already contain something at the moment of this add?
   *
   * Read from Rebuy's own state, because that is exactly what Smart Cart renders from —
   * the Shopify cart could be ahead of it and we would still paint an empty drawer.
   * Uses the documented Rebuy.Cart.itemCount(), falling back to the cart object.
   * Defaults to FALSE (the cautious path) if we can't tell.
   */
  function cartHadItems() {
    try {
      var c = window.Rebuy && window.Rebuy.Cart;
      if (c && typeof c.itemCount === 'function') return c.itemCount() > 0;
      if (c && c.cart) return (c.cart.item_count || 0) > 0;
    } catch (e) {}
    return false;
  }

  // Set while an add into an EMPTY cart is settling. Suppresses our own reveal paths
  // so Smart Cart opens itself, populated, exactly as it does today.
  var deferUntil = 0;
  function deferringToRebuy() { return Date.now() < deferUntil; }

  /**
   * Empty-cart path: DON'T open. Let Rebuy open the cart itself when it's ready.
   *
   * Measured on this theme (mobile, Fast-4G + 4x CPU): Smart Cart does not render an
   * added line item until ~1.7-2.0s after the add, no matter what we do.
   * `rebuy:cart.change` is NOT a usable "safe to show" signal — in one run it fired at
   * 214ms while the item did not appear in the DOM until 1739ms. So every attempt to
   * open early on an empty cart paints "YOUR CART IS EMPTY" first:
   *
   *   open at the 1500ms cap -> empty for ~230ms
   *   open on cart.change     -> empty for ~1380ms
   *
   * Baseline already opens correctly-populated at ~1.8s, so there is simply no speed
   * win available here. Deferring costs nothing and keeps the drawer honest — and the
   * real gain on this path is the button spinner, which the theme had no equivalent of
   * at all (baseline measures NO affordance whatsoever).
   *
   * Backstop: if Rebuy hasn't opened the cart by DEFER_MAX_MS, we open it ourselves —
   * by then the item has long since rendered, so it is safe.
   */
  var DEFER_MAX_MS = CFG.deferMaxMs || 3000;
  function deferToRebuy() {
    deferUntil = Date.now() + DEFER_MAX_MS;
    setTimeout(function () {
      deferUntil = 0;
      if (!cartIsVisible()) revealDebounced();
    }, DEFER_MAX_MS);
  }

  function rebuyPresent() { return !!window.Rebuy; }

  function smartCartReady() {
    try {
      var sc = window.Rebuy && window.Rebuy.SmartCart;
      return !!(sc && sc.status === 'ready' && typeof sc.show === 'function');
    } catch (e) { return false; }
  }

  function cartIsVisible() {
    return document.body.classList.contains('rebuy-cart-visible') ||
           document.body.classList.contains('rebuy-modal-visible');
  }

  function resync() {
    try {
      // Required for XHR adds that Rebuy's fetch wrapper never observed.
      if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') {
        window.Rebuy.Cart.fetchCart();
      }
    } catch (e) {}
  }

  function flushOnRevealed() {
    var fns = pendingOnRevealed.splice(0);
    for (var i = 0; i < fns.length; i++) {
      try { fns[i](); } catch (e) {}
    }
  }

  function showSmartCart() {
    try {
      window.Rebuy.SmartCart.show();
      lastRevealAt = Date.now();
    } catch (e) {
      runFallback();
      return;
    }
    // Populate after opening. This is the "open then fill" half.
    if (RESYNC_DELAY_MS > 0) setTimeout(resync, RESYNC_DELAY_MS);
    else resync();
  }

  function runFallback() {
    if (FALLBACK === 'cart') { window.location.assign('/cart'); return; }
    if (FALLBACK === 'confirm') {
      // rebuy-atc-feedback.js owns the button state; tell it the add landed.
      document.dispatchEvent(new CustomEvent('rebuy-bridge:fallback'));
      return;
    }
  }

  function waitForReadyThen(showFn, fallbackFn) {
    if (waiting) return;
    waiting = true;
    var start = Date.now();
    (function check() {
      if (smartCartReady()) { waiting = false; return showFn(); }
      if (Date.now() - start >= READY_TIMEOUT_MS) { waiting = false; return fallbackFn(); }
      setTimeout(check, READY_POLL_MS);
    })();
  }

  // opts.onRevealed — invoked once the cart is actually visible, or when the
  // bounded wait gives up. Used by the Wonder theme's product-form.js to hold its
  // native button spinner up until the cart is genuinely on screen.
  function reveal(opts) {
    if (opts && typeof opts.onRevealed === 'function') pendingOnRevealed.push(opts.onRevealed);

    if (cartIsVisible()) { resync(); flushOnRevealed(); return; }
    if (smartCartReady()) { showSmartCart(); return; }
    if (rebuyPresent()) { waitForReadyThen(showSmartCart, function () { runFallback(); flushOnRevealed(); }); return; }
    runFallback();
    flushOnRevealed();
  }

  function revealDebounced(opts) {
    clearTimeout(revealTimer);
    revealTimer = setTimeout(function () { reveal(opts); }, REVEAL_DEBOUNCE_MS);
  }

  /* ------------------------- trigger: the add is in flight ------------------------- */

  // Wrap XHR minimally so we learn the instant the theme's add is sent. This runs
  // from <head>, so our wrapper is innermost; Gobot / Shopify web-pixels wrap
  // around it and still work.
  if (REVEAL_ON === 'add-sent') {
    var xo = XMLHttpRequest.prototype.open;
    var xs = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__rebuyBridgeUrl = url; } catch (e) {}
      return xo.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var self = this;
      try {
        if (/\/cart\/add/.test(String(self.__rebuyBridgeUrl || ''))) {
          lastAddAt = Date.now();

          // CONDITIONAL OPEN — do not "simplify" this to an unconditional reveal.
          //
          // Smart Cart renders from its OWN cart state, which lags the add by ~2s. If
          // the cart was EMPTY, opening now paints "YOUR CART IS EMPTY" at the exact
          // moment the shopper just added something, and it stays that way until the
          // item lands. Measured on the sibling Velour theme: open at t+150ms, still
          // empty at t+400ms, item finally rendered at t+800ms.
          //
          // Waiting unconditionally is not the answer either — `rebuy:cart.change`
          // lands ~2s after the add, which erases the whole improvement (measured).
          //
          // So: cart already had items -> open immediately, because those items render
          // straight away and nothing misleading is shown. Cart was empty -> wait for
          // Rebuy's state to settle, so we never tell someone their cart is empty right
          // after they filled it. Repeat adds (the common case) keep the fast path.
          if (cartHadItems()) {
            revealDebounced();
            self.addEventListener('load', function () { resync(); });
          } else {
            self.addEventListener('load', function () { resync(); });
            deferToRebuy();
          }
        }
      } catch (e) {}
      return xs.apply(this, arguments);
    };
  }

  if (REVEAL_ON === 'click') {
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest(ATC_SELECTOR);
      if (!btn || btn.disabled || btn.classList.contains('disabled')) return;
      if (document.body.classList.contains('focus-enabled')) return; // theme does a native POST
      lastAddAt = Date.now();
      revealDebounced();
    }, true);
  }

  // Safety net for add paths we don't observe directly (the theme's grid quick-add,
  // other apps), and the trigger itself in 'cart-change' mode.
  //
  // ⚠️ Gated on sawRecentAdd(). Rebuy fires `rebuy:cart.change` repeatedly during its
  // own initialisation with no user interaction at all — verified on this store at
  // +6.1s / +8.1s / +9.4s. Revealing on an ungated cart-change opens the Smart Cart
  // spontaneously on page load and drops its overlay over the ATC button. Never
  // reveal unless we actually observed an add.
  document.addEventListener('rebuy:cart.change', function () {
    if (cartIsVisible()) { flushOnRevealed(); return; }
    if (REVEAL_ON === 'manual') return;
    if (!sawRecentAdd()) return;                   // init noise, not a shopper action
    if (deferringToRebuy()) return;                // empty-cart add: Rebuy opens it, populated
    if (Date.now() - lastRevealAt < 2000) return;  // we already opened for this add
    revealDebounced();
  });

  // The theme's own post-add event, dispatched by updateHeaderTotal after its
  // follow-up fetch('/cart.js'). Last-resort trigger if the XHR hook ever misses.
  // The theme only dispatches this after a real add, but gate it anyway for symmetry.
  document.addEventListener('theme:cart:change', function () {
    if (REVEAL_ON === 'manual') return;
    if (cartIsVisible()) return;
    if (!sawRecentAdd()) return;
    if (deferringToRebuy()) return;                // empty-cart add: Rebuy opens it, populated
    if (Date.now() - lastRevealAt < 2000) return;
    revealDebounced();
  });

  // The cart became visible — release anyone waiting on it.
  (function watchBody() {
    if (!document.body) { return void requestAnimationFrame(watchBody); }
    new MutationObserver(function () {
      if (cartIsVisible()) flushOnRevealed();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  })();

  /* ------------------------- cart icon: never swallow a click ------------------------- */

  // Smart Cart V2 binds the cart icon and preventDefaults, but BAILS when
  // status !== 'ready' — so a pre-ready click is silently dead. There is no
  // native drawer on this theme, so we own the click and guarantee an outcome.
  document.addEventListener('click', function (e) {
    var trigger = e.target && e.target.closest && e.target.closest(CART_ICON_SELECTOR);
    if (!trigger) return;
    if (trigger.hasAttribute('disable-rebuy') || trigger.classList.contains('disable-rebuy')) return;
    if (!rebuyPresent()) return; // Rebuy off → let the theme do whatever it normally does

    e.preventDefault();
    e.stopImmediatePropagation();

    if (smartCartReady()) { showSmartCart(); return; }
    waitForReadyThen(showSmartCart, runFallback);
  }, true);

  /* ---------------------------------- public API ---------------------------------- */

  window.RebuyCartBridge = {
    reveal: reveal,
    revealDebounced: revealDebounced,
    ready: smartCartReady,
    visible: cartIsVisible,
    resync: resync
  };
})();
